import { useMemo } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

import { cx } from '@/styles/muiTheme';

import { countDiff, diffLines } from './diffLines';

interface ConfigDiffReviewProps {
  /** Pretty-printed saved value. */
  before: string;
  /** Pretty-printed draft. */
  after: string;
  reason: string;
  onReasonChange: (reason: string) => void;
  requireReason: boolean;
  nextVersion: number;
  onCancel: () => void;
  onConfirm: () => void;
  isSaving: boolean;
  /** A server rejection from the previous attempt, rendered above the diff. */
  serverError: string | null;
}

const LINE_STYLES: Record<
  string,
  { background: string; color: string; mark: string; markColor: string }
> = {
  added: {
    background: 'rgba(18,183,106,0.12)',
    color: cx.text.primary,
    mark: '+',
    markColor: cx.green,
  },
  removed: {
    background: 'rgba(240,68,56,0.12)',
    color: cx.text.primary,
    mark: '−',
    markColor: cx.red.main,
  },
  context: {
    background: 'transparent',
    color: cx.text.tertiary,
    mark: ' ',
    markColor: cx.text.muted,
  },
};

/**
 * The confirm step: what is about to change, before it changes.
 *
 * Diffing the pretty-printed forms rather than the raw stored text is
 * deliberate. A value saved by an older tool may have different whitespace or
 * key order, and a diff full of formatting noise trains operators to skim the
 * one line that actually matters.
 */
const ConfigDiffReview = ({
  before,
  after,
  reason,
  onReasonChange,
  requireReason,
  nextVersion,
  onCancel,
  onConfirm,
  isSaving,
  serverError,
}: ConfigDiffReviewProps) => {
  const lines = useMemo(() => diffLines(before, after), [before, after]);
  const counts = useMemo(() => countDiff(lines), [lines]);

  const reasonMissing = requireReason && !reason.trim();
  const canConfirm = !isSaving && !reasonMissing;

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ color: cx.text.secondary }}>
          Review changes — saves as v{nextVersion}
        </Typography>
        <Typography variant="caption" sx={{ color: cx.green }}>
          +{counts.added}
        </Typography>
        <Typography variant="caption" sx={{ color: cx.red.main }}>
          −{counts.removed}
        </Typography>
      </Stack>

      {serverError && <Alert severity="error">{serverError}</Alert>}

      <Box
        sx={{
          border: `1px solid ${cx.border.strong}`,
          borderRadius: 1,
          backgroundColor: cx.bg.surface3,
          maxHeight: 420,
          overflow: 'auto',
          fontFamily: cx.fontMono,
          fontSize: 12,
          lineHeight: '18px',
        }}
      >
        {lines.map((line, index) => {
          const style = LINE_STYLES[line.kind];

          return (
            // Diff output is positional and regenerated whole on every change,
            // so the index is the only stable identity a line has.
            <Box key={index} sx={{ display: 'flex', backgroundColor: style.background }}>
              <Box
                component="span"
                sx={{ width: 20, textAlign: 'center', color: style.markColor, flexShrink: 0 }}
              >
                {style.mark}
              </Box>
              <Box component="span" sx={{ color: style.color, whiteSpace: 'pre-wrap' }}>
                {line.text}
              </Box>
            </Box>
          );
        })}
      </Box>

      <TextField
        size="small"
        fullWidth
        label={requireReason ? 'Reason (required)' : 'Reason (optional)'}
        placeholder="Why is this changing?"
        value={reason}
        onChange={(event) => onReasonChange(event.target.value)}
        helperText="Stored on the version. This is what the history list shows a year from now."
      />

      <Stack direction="row" sx={{ gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={isSaving} sx={{ color: cx.text.tertiary }}>
          Back to editing
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={!canConfirm}
          sx={{ backgroundColor: cx.blue.main, '&:hover': { backgroundColor: cx.blue.dark } }}
        >
          {isSaving ? 'Saving…' : `Save as v${nextVersion}`}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ConfigDiffReview;
